import { AutoAwesome } from '@mui/icons-material'
import { Button, CircularProgress, Grid, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { getGrantFitSummariesForFunder, runGrantFitAnalysis } from 'store/actions/grantfit'
import { selectGrantFit } from 'store/selectors/grantfit'
import { selectFunderProfileInfo } from 'store/selectors/profile'

import { trackGrantFitSummarizeButtonClick } from 'utils/mixpanel'

import GrantFitFileUpload from './components/GrantFitFileUpload'
import GrantFitSummary from './components/GrantFitSummary'
import FunderPrograms from 'components/FunderPrograms'

const ProfileGrantFit = ({ gotoTab, extra }) => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const { id: funderId, funderPrograms, isLoading } = useSelector(selectFunderProfileInfo)
  const { summaries, error } = useSelector(selectGrantFit)

  const [state, setState] = useState({
    programIndex: 0,
    uploadState: {
      selectedFiles: [],
      errors: null
    },
    applicantText: '',
    error: null,
    isProcessing: false
  })

  const hasPrograms = funderPrograms?.length > 0
  const summary = hasPrograms
    ? summaries.find(item => item.program === funderPrograms[state.programIndex].id)
    : summaries.at(0)

  const programId = +extra

  useEffect(() => {
    dispatch(getGrantFitSummariesForFunder(funderId))
  }, [])

  useEffect(() => {
    if (isLoading) return
    const index = funderPrograms.findIndex(program => program.id === programId)
    if (index >= 0) {
      setState(state => ({ ...state, programIndex: index }))
    } else {
      gotoTab(ProfileGrantFit)
    }
  }, [programId, funderPrograms])

  useEffect(() => {
    if (state.isProcessing) {
      if (summary && !summary.isProcessing) {
        setState(state => ({ ...state, isProcessing: false }))
      }
    } else {
      if (summary) {
        setState(state => ({
          ...state,
          uploadState: {
            selectedFiles: summary.documents.map(doc => ({
              id: doc.id,
              name: doc.originalFilename,
              size: doc.fileSize
            })),
            errors: null
          },
          applicantText: summary.applicantText,
          error: null
        }))
      } else {
        setState(state => ({
          ...state,
          uploadState: {
            selectedFiles: [],
            errors: null
          },
          applicantText: '',
          error: null
        }))
      }
    }
  }, [summary])

  useEffect(() => {
    if (error) {
      setState(state => ({ ...state, isProcessing: false }))
    }
  }, [error])

  const onProgramIndexChange = newIndex => {
    if (state.isProcessing) return
    setState(state => ({ ...state, programIndex: newIndex }))
    gotoTab(ProfileGrantFit, [funderPrograms[newIndex].id])
  }

  const setUploadState = uploadState => {
    setState(state => ({ ...state, uploadState, error: null }))
  }

  const onApplicantTextChange = event => {
    setState(state => ({ ...state, applicantText: event.target.value, error: null }))
  }

  const onSubmit = () => {
    if (state.isProcessing) {
      setState(state => ({ ...state, error: t.funder.grantFit.errors.analysisInProgress }))
      return
    }
    if (!state.applicantText && state.uploadState.selectedFiles.length === 0) {
      setState(state => ({ ...state, error: t.funder.grantFit.errors.noDetails }))
      return
    }
    if (state.uploadState.errors?.length > 0) {
      setState(state => ({ ...state, error: t.funder.grantFit.errors.fixFileIssues }))
      return
    }

    const programId = hasPrograms ? funderPrograms[state.programIndex].id : undefined

    setState(state => ({ ...state, isProcessing: true }))
    dispatch(
      runGrantFitAnalysis({
        funderId,
        programId,
        applicantText: state.applicantText,
        files: state.uploadState.selectedFiles.filter(file => file.file),
        documents: state.uploadState.selectedFiles.filter(file => file.id)
      })
    )

    trackGrantFitSummarizeButtonClick(programId)
  }

  return (
    <Grid container className="profile-grantfit" columns={24}>
      {hasPrograms && (
        <Grid size={{ xs: 24, sm: 7, md: 6, lg: 5 }}>
          <FunderPrograms
            funderPrograms={funderPrograms}
            programIndex={state.programIndex}
            setProgramIndex={onProgramIndexChange}
          />
        </Grid>
      )}
      <Grid size="grow" className="profile-grantfit-content">
        <div className="section">
          <p className="intro">{t.funder.grantFit.descriptionText}</p>
          <p>
            <i>{t.funder.grantFit.disclaimerText}</i>
          </p>
        </div>
        <div className="section">
          <h3>{t.funder.grantFit.uploadDocumentsTitle}</h3>
          <GrantFitFileUpload uploadState={state.uploadState} setUploadState={setUploadState} />
        </div>
        <div className="section">
          <h3>{t.funder.grantFit.projectDetailsTitle}</h3>
          <p>{t.funder.grantFit.projectDetailsText}</p>
          <TextField
            className="grantfit-textarea"
            value={state.applicantText}
            onChange={onApplicantTextChange}
            multiline
            minRows={3}
            maxRows={10}
            fullWidth
            placeholder={t.funder.grantFit.projectDetailsPlaceholder}
            slotProps={{ htmlInput: { maxLength: 100000 } }}
          />
        </div>
        <Grid container columnGap={2} className={`grantfit-submit ${state.error ? 'error' : ''}`}>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={state.isProcessing}
            endIcon={state.isProcessing && <CircularProgress size={16} />}
          >
            {t.funder.grantFit.summarizeFitButton}
          </Button>
          {state.isProcessing && <p>{t.funder.grantFit.pleaseWait}</p>}
          {state.error && <p>{state.error}</p>}
        </Grid>
        <GrantFitSummary summary={summary} />
      </Grid>
    </Grid>
  )
}

ProfileGrantFit.isEnabled = ({ isFunderDissolved, user }) => !isFunderDissolved && user.grantfitBeta
ProfileGrantFit.tabPath = 'grantfit'
ProfileGrantFit.getTabName = t => (
  <>
    <AutoAwesome />
    {t.funder.grantFit.tab}
  </>
)

export default ProfileGrantFit
